import type { ReactNode } from "react";
import { Link } from "react-router";
import { ArrowLeft } from "lucide-react";

import { Icon } from "./Icon";
import { SectionTabs, type Section } from "./SectionTabs";
import { Skeleton } from "./Skeleton";

/**
 * The header of a page — the same on all of them, so that "where am I, and how do I get out?" is
 * answered in the same place every time:
 *
 *     ‹ Templates                       (back: a secondary page — OR — SECTION: a page of a section)
 *     welcome-email  [Draft]                                            [ Cancel ] [ Save ]
 *     A line that says what the page is for.
 *
 *     and, on a page of a section, the tabs come BEFORE the line:
 *
 *     MESSAGING
 *     Templates                                                            [ New template ]
 *     Emails · Templates · Campaigns
 *     A line that says what the page is for.
 *
 * The h1 names THE PAGE ("Emails", "welcome-email"), never the section: the section is the eyebrow
 * above it and the tabs under it, and the sidebar shows it too. Every page has exactly one.
 *
 *   title     the page's name. `undefined` means "still loading" (a page named after the object it
 *             shows): a bar of the title's size holds the place, so nothing moves when it arrives.
 *   section   a page of a section that has several (Messaging, Catalog, Factory): the section's name
 *             above the title and its tabs right under it. Not on a secondary page. The bar sits
 *             right after the title line and BEFORE the subtitle, so it is in the same place on every
 *             tab of the section: a subtitle of one line on one tab and of three on the next would
 *             otherwise move it under the pointer that has just clicked it.
 *   back      a secondary page — an editor, a detail: a link to its parent, ALWAYS in the same place,
 *             in the place of the eyebrow. It navigates in the app (never a full reload: the sign-in
 *             lives in memory), and says WHERE it goes ("Templates", a tenant's name), not "Back".
 *             It stays on ONE line (the name is cut with an ellipsis and complete in the tooltip):
 *             a name that comes from the data must not change the header's height when it arrives.
 *   subtitle  one line on what the page is for, or a figure ("12 operations").
 *   status    the state of the object, as pills next to the title.
 *   actions   what the operator does here, on the right; they wrap under the title on a narrow window.
 *
 * The header keeps the same height on every page of a kind: the title line is 36 px tall whether or
 * not it has buttons, and a long title wraps instead of pushing the buttons out.
 */
type Position =
  | { section?: Section; back?: never }
  | { back?: { to: string; label: string }; section?: never };

type PageHeaderProps = Position & {
  title: ReactNode;
  subtitle?: ReactNode;
  status?: ReactNode;
  actions?: ReactNode;
};

export function PageHeader({ title, section, back, subtitle, status, actions }: PageHeaderProps) {
  return (
    <header className="mb-6">
      <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-3">
        <div className="min-w-0">
          {back ? (
            <div className="mb-1.5">
              <Link
                to={back.to}
                title={back.label}
                className="inline-flex max-w-full items-center gap-1.5 text-sm font-medium text-brand-600 hover:underline"
              >
                <Icon icon={ArrowLeft} />
                <span className="min-w-0 truncate">
                  <span className="sr-only">Back to </span>
                  {back.label}
                </span>
              </Link>
            </div>
          ) : section ? (
            <p className="mb-0.5 text-xs font-semibold uppercase leading-4 tracking-wider text-ink-muted">
              {section.label}
            </p>
          ) : null}
          <div className="flex min-h-9 flex-wrap items-center gap-x-3 gap-y-1">
            {title === undefined ? (
              <>
                <h1 className="sr-only">Loading…</h1>
                <Skeleton className="h-6 w-56" />
              </>
            ) : (
              <h1 className="min-w-0 break-words text-2xl font-semibold text-ink">{title}</h1>
            )}
            {status}
          </div>
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {section && <SectionTabs label={`${section.label} pages`} tabs={section.tabs} className="mt-4" />}
      {subtitle && (
        <div className={`${section ? "mt-3" : "mt-1"} max-w-3xl text-sm text-ink-muted`}>{subtitle}</div>
      )}
    </header>
  );
}
